import * as THREE from 'three'; 
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { Water } from 'three/addons/objects/Water.js';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const isLowPower = isMobile || navigator.hardwareConcurrency <= 2;

const getRenderSize = () => {
  return { width: window.innerWidth, height: window.innerHeight };
};

class OutdoorScene {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    this.scene = null;
    this.camera = null;
    this.object = null;
    this.renderer = null;
    this.mixer = null;
    this.clock = new THREE.Clock();
    this.cameras = {
      start: null,
      window: null,
      arcade: null,
      current: null,
    };

    this.isDragging = false;
    this.previousMousePosition = { x: 0, y: 0 };
    this.rotationSpeed = 0.002;

    this.targetRotation = { x: 0, y: 0 };
    this.currentRotation = { x: 0, y: 0 };
    this.dampingFactor = 0.1;

    this.minVerticalAngle = -Math.PI / 3;
    this.maxVerticalAngle = Math.PI / 3;

    this.cameraPath = null;
    this.isAnimating = false;
    this.animationProgress = 0;

    // Raycasting
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.clickableObjects = [];
    this.hoveredObject = null;

    this.dayNightCycle = true;
    this.dayNightSpeed = 0.15;
    this.minBrightness = 0.2;
    this.maxBrightness = 0.5;

    this.nightColor = new THREE.Color(0x2a1a3a);
    this.dayColor = new THREE.Color(0x8a7eb5);
    this.sunsetColor = new THREE.Color(0xb565a7);

    this.nightFogColor = new THREE.Color(0x2a1a3a); 
    this.dayFogColor = new THREE.Color(0x8a7eb5);

    this.floorEmission = null;
    this.IndoorLights = [];

    this.textMesh = null;
    this.textBlinkTime = 0;

    this.composer = null;  
    this.bloomPass = null;
  }
  
  init(loadingManager = null, preloaderAssets = null) {
    this.loadingManager = loadingManager;
    this.preloaderAssets = preloaderAssets;

    if (!this.container) {
      console.error('outdoor-container not found');
      return;
    }

    this.container.innerHTML = '';

    // Scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0x2a1a3a, 400, 1500);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: !isLowPower,
      alpha: false,
      powerPreference: isLowPower ? 'low-power' : 'default',
      failIfMajorPerformanceCaveat: false
      
    });
    this.renderer.shadowMap.enabled = false

    const { width, height } = getRenderSize();
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(isLowPower ? 1 : Math.min(window.devicePixelRatio, 2));
    
    // Configuration HDR
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    
    this.container.appendChild(this.renderer.domElement);

    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    if (w > 0 && h > 0) {
      this.renderer.setSize(w, h);
    }

    console.log('Bloom/Glow activated');

    // Setup cameras
    this.setupCameras();

    // ✅ Setup post-processing glow/néon effect
    this.composer = new EffectComposer(this.renderer);

    const renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(renderPass);

    const bloomStrength = isMobile ? 0.6 : 2;
    const bloomRadius = isMobile ? 0.15 : 0.4;
    const bloomThreshold = isMobile ? 0.97 : 0.95;

    const size = getRenderSize();
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(size.width, size.height),
      bloomStrength,    // strength (intensity of the glow)
      bloomRadius,    // radius (ray of theglow)
      bloomThreshold    // threshold (oglowy objects > 0.85 glow)
    );

    const JPG_MOBILE_URL = '/static/textures/sci-fi-alien-planet-landscpe_1K_5d4ee6cb-e68d-4172-aa98-c8e70879791c.jpg';
    if (isMobile){
      this.bloomPass.resolution.multiplyScalar(0.2);  // 50% of the resolution
      // or this.bloomPass.resolution.set(window.innerWidth * 0.5, window.innerHeight * 0.5);
      this.renderer.domElement.style.width = '100%';
      this.renderer.domElement.style.height = '100%';
      const jpgTexture = preloaderAssets?.[JPG_MOBILE_URL];
      if(jpgTexture) {
      jpgTexture.mapping = THREE.EquirectangularReflectionMapping;
      this.scene.background = jpgTexture;
      this.renderer.setClearColor(0xb565a7, 0.60);
      } else {
          new THREE.TextureLoader().load(JPG_MOBILE_URL, (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            this.scene.background = texture;
            this.renderer.setClearColor(0xb565a7, 0.60);
        });
      }
    } else {
      if (preloaderAssets?.exrTexture) {
        const exrTexture = preloaderAssets?.exrTexture;
        exrTexture.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.background = exrTexture;
        this.scene.environment = exrTexture;
        this.renderer.setClearColor(0xb565a7, 0.60);
      } else {
        this.loadEXR();  // Fallback
      }
    }
    
    if (preloaderAssets?.gltf) {
        this.processModel(preloaderAssets.gltf);  // ← Traiter sans recharger
    } else {
        this.loadModel();  // Fallback
    }

    this.composer.addPass(this.bloomPass);

    // Controls
    this.setupControls();

    this.setupPaths();

    this.setupNavigation();
    
    this.createOcean();
    
    // Responsive
    window.addEventListener('resize', () => this.onResize());
    
    // Start
    this.animate();

    this.setupInteractions();
  } 
  
  loadEXR() {
    const loader = new EXRLoader(this.loadingManager || undefined);
    
    loader.load(
      isLowPower ? '/static/textures/sci-fi-alien-planet-landscpe_1K_5d4ee6cb-e68d-4172-aa98-c8e70879791c.exr' :
      '/static/textures/sci-fi-alien-planet-landscpe_1K_5d4ee6cb-e68d-4172-aa98-c8e70879791c.exr',

      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        this.scene.background = texture;
        this.scene.environment = texture;

        if (this.scene.backgroundIntensity !== undefined) {
          this.scene.backgroundIntensity = 0.5;  
        }

        if (this.scene.backgroundIntensity !== undefined) {
          this.scene.backgroundIntensity = 0.5;  
        }
        this.renderer.setClearColor(0xb565a7, 0.60);
        console.log('EXR loaded');
      },
      (xhr) => {
        if (xhr.lengthComputable && xhr.total > 0) {
          const percent = (xhr.loaded / xhr.total * 100).toFixed(1);
          console.log(`EXR: ${percent}% chargé`);
        }
      },
      (error) => {
        console.error('EXR error:', error);
      }
    );
  }
  
  loadModel() {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    
    const gltfLoader = new GLTFLoader(this.loadingManager || undefined);
    gltfLoader.setDRACOLoader(dracoLoader);
    
    gltfLoader.load(
      '/static/models/General_3D_model_whitoutTextureCamerasAnimations17.glb',
      (gltf) => {
        this.scene.add(gltf.scene);
        
        let floorFound = false;

        gltf.scene.traverse((child) => {

          if (child.name && child.name.toLowerCase().includes('text')) {
            console.log('Text trouvé:', child.name);
            
            // Modify the material for green emissive
            if (child.material) {
              child.material.color = new THREE.Color(0x00ff00);  // greenish
              child.material.emissive = new THREE.Color(0x00ff00);
              child.material.emissiveIntensity = 2.0;
            }
            
            this.textMesh = child;  // Store for the animation
            console.log('✅ Text configuré en vert');
          }

          if (child.name && child.name.toLowerCase().includes('arcadebutton')) {
            console.log('🔘 Bouton arcade found:', child.name);
            
            // Add an emissive material to make it visible
            if (child.material) {
              child.material.emissive = new THREE.Color(0x00ff00);
              child.material.emissiveIntensity = 1.5;
            }
            
            this.arcadeButton = child;
          }


          if (child.isMesh && child.name.toLowerCase().includes('floor')) {
            console.log('💡 floor found:', child.name);
            console.log('📍 Position X:', child.position.x);
            console.log('📍 Position Y:', child.position.y);  // ← Important !
            console.log('📍 Position Z:', child.position.z);
            console.log('📦 World position:', child.getWorldPosition(new THREE.Vector3()));
            this.floorEmission = child;
            floorFound = true;
            
            // Create the linear lights
            this.createIndoorLights(child.position);
          }
        });

        if (!floorFound) {
          console.log('floor not found, creation on the center');
          this.createIndoorLights(new THREE.Vector3(0, 0, 0));
        }

        if (gltf.animations.length > 0) {
          this.mixer = new THREE.AnimationMixer(gltf.scene);
          gltf.animations.forEach(clip => {
            this.mixer.clipAction(clip).play();
          });
        }
      },
      (xhr) => {
        if (xhr.lengthComputable && xhr.total > 0) {
          const percent = (xhr.loaded / xhr.total * 100).toFixed(1);
          console.log(`Model: ${percent}% chargé`);
        }
      },
      (error) => {
        console.error('Error model loading:', error);
      }
    );
  }

  processModel(gltf) {
    this.scene.add(gltf.scene);
    
    let floorFound = false;
  
    gltf.scene.traverse((child) => {
      if (child.name && child.name.toLowerCase().includes('text')) {
        if (child.material) {
          child.material.color = new THREE.Color(0x00ff00);
          child.material.emissive = new THREE.Color(0x00ff00);
          child.material.emissiveIntensity = 2.0;
        }
        this.textMesh = child;
      }
  
      if (child.name && child.name.toLowerCase().includes('arcadeButton')) {
        if (child.material) {
          child.material.emissive = new THREE.Color(0x00ff00);
          child.material.emissiveIntensity = 1.5;
        }
        this.arcadeButton = child;
      }
  
      if (child.isMesh && child.name.toLowerCase().includes('floor')) {
        this.floorEmission = child;
        floorFound = true;
        this.createIndoorLights(child.position);
      }
    });
  
    if (!floorFound) {
      this.createIndoorLights(new THREE.Vector3(0, 0, 0));
    }
  
    if (gltf.animations.length > 0) {
      this.mixer = new THREE.AnimationMixer(gltf.scene);
      gltf.animations.forEach(clip => {
        this.mixer.clipAction(clip).play();
      });
    }
  }

  createOcean() {
    console.log('Création océan THREE.js');
    
    // Create the plane geometry (size to adjust according to your scene)
    const segments = isLowPower ? 32 : 128;  // 32×32 = 1024 triangles au lieu de 32768
  const waterGeometry = new THREE.PlaneGeometry(10000, 10000, segments, segments);
    
    const water = new Water(
      waterGeometry,
      {
        textureWidth: isLowPower ? 256 : 1024,
        textureHeight: isLowPower ? 256 : 1024,
        waterNormals: new THREE.TextureLoader(this.loadingManager || undefined).load('/static/textures/waternormals.jpg', 
          (texture) => {
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
          }),
        sunDirection: new THREE.Vector3(0.5, 0.3, 0.8),
        sunColor: 0xffffff,       // white
        waterColor: 0x1a2a3a,     // dark violet
        distortionScale: 40,    // strong waves
        size: 8,
        alpha: 0.85,               // well visible
        fog: this.scene.fog !== undefined
      }
    );

    console.log('Océan tempête créé');
    console.log('Position:', water.position);
    console.log('Segments:', waterGeometry.parameters);
    
    // Manual position and rotation
    water.rotation.x = -Math.PI / 2;  // Horizontal
    water.position.set(0, 10, 0);
    
    this.scene.add(water);
    this.water = water;
    
    console.log('Ocean created at:', water.position);
  }

createIndoorLights(floorPosition) {
  this.indoorLights = [];
  
  const hemiLight = new THREE.HemisphereLight(0xffd4a3, 0x2a1a3a, 0.6);
  this.scene.add(hemiLight);
  this.indoorLights.push(hemiLight);

  // Lines in Z (tleft/right)
  const leftLineZ = floorPosition.z - 118;
  const rightLineZ = floorPosition.z + 130;
  
  const numlights = isLowPower ? 5 : 10;
  const spacingX = 10;
  
  //light position height
  const lightHeight = floorPosition.y + 15;
  
  // ✅ CENTRER SUR X = 0 (entre vos caméras et l'indoor)
  // Ou utilisez X = -50 pour être encore plus proche de vous
  const centerX = -200;  // Au lieu de floorPosition.x (108.58)
  const startX = centerX - (spacingX * (numlights - 1) / 2);
  
  console.log('Centre X =', centerX);
  console.log('Zone X: de', startX, 'à', (startX + spacingX * (numlights - 1)));


  // LIGNE AVANT
  const lightA = new THREE.PointLight(0xffd4a3, 30, 800);
  lightA.position.set(centerX, lightHeight, floorPosition.z - 118);
  lightA.castShadow = false;
  this.scene.add(lightA);
  this.indoorLights.push(lightA);

  // BACK LINE
  const lightB = new THREE.PointLight(0xffd4a3, 30, 800);
  lightB.position.set(centerX + 70, lightHeight, floorPosition.z + 130);
  lightB.castShadow = false;
  this.scene.add(lightB);
  this.indoorLights.push(lightB);

  const frontWallX = centerX + 70;
  const startZ = leftLineZ + 5 ;   
  const endZ = rightLineZ;    
  const spacingZ = (endZ - startZ) / (numlights - 1); 

  for (let i = 0; i < numlights; i++) {
    const zPos = startZ + (i * spacingZ);
    
    const lightWindow = new THREE.PointLight(0xffd4a3, 80, 1000);  // Plus intense
    lightWindow.position.set(frontWallX, lightHeight, zPos); 
    lightWindow.castShadow = false;
    this.scene.add(lightWindow);
    this.indoorLights.push(lightWindow);
    }
  }
  
  setupControls() {
    this.setupFreeLookControls();
  }

  setupFreeLookControls() {
    const canvas = this.renderer.domElement;
    // Mouse down - start looking around
    canvas.addEventListener('mousedown', (event) => {
      this.isDragging = true;
      this.previousMousePosition = {
        x: event.clientX,
        y: event.clientY
      };
      canvas.style.cursor = 'grabbing';
    });
    
    // Mouse move - turn the head
    canvas.addEventListener('mousemove', (event) => {
      if (!this.isDragging || this.isAnimating) return;
      
      const deltaX = event.clientX - this.previousMousePosition.x;
      const deltaY = event.clientY - this.previousMousePosition.y;
      
      // Horizontal rotation (left/right) - Y axis
      this.targetRotation.y -= deltaX * this.rotationSpeed;
      
      // Vertical rotation (up/down) - X axis
      this.targetRotation.x -= deltaY * this.rotationSpeed;
      
      // Limit the vertical rotation (don't turn around)
      this.targetRotation.x = Math.max(
        this.minVerticalAngle,
        Math.min(this.maxVerticalAngle, this.targetRotation.x)
      ); 
      this.previousMousePosition = {
        x: event.clientX,
        y: event.clientY
      };
    });
    
    // Mouse up - stop looking
    canvas.addEventListener('mouseup', () => {
      this.isDragging = false;
      canvas.style.cursor = 'grab';
    });
    
    // Mouse leave - stop if the mouse leaves the canvas
    canvas.addEventListener('mouseleave', () => {
      this.isDragging = false;
      canvas.style.cursor = 'default';
    });

    canvas.addEventListener('touchstart', (event) => {
      if (event.touches.length === 1) {
        this.isDragging = true;
        this.previousMousePosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
      }
    }, { passive: true });
    
    canvas.addEventListener('touchmove', (event) => {
      if (event.touches.length === 1 && this.isDragging && !this.isAnimating) {
        event.preventDefault();
        const deltaX = event.touches[0].clientX - this.previousMousePosition.x;
        const deltaY = event.touches[0].clientY - this.previousMousePosition.y;
        this.targetRotation.y -= deltaX * this.rotationSpeed;
        this.targetRotation.x -= deltaY * this.rotationSpeed;
        this.targetRotation.x = Math.max(this.minVerticalAngle, Math.min(this.maxVerticalAngle, this.targetRotation.x));
        this.previousMousePosition = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
    }, { passive: false });
    
    canvas.addEventListener('touchend', (event) => {
      if (event.touches.length === 0) this.isDragging = false;
    }, { passive: true });
    
    canvas.addEventListener('touchcancel', (event) => {
      if (event.touches.length === 0) this.isDragging = false;
    }, { passive: true });
    
    canvas.style.cursor = 'grab';;
  
    console.log('FreeLook controls activated');
  }

  setupInteractions() {
    const canvas = this.renderer.domElement;
  
    canvas.addEventListener('click', (event) => {
      if (this.isAnimating) return;
      
      // Normalized mouse position
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      // Raycasting
      this.raycaster.setFromCamera(this.mouse, this.camera);
      const intersects = this.raycaster.intersectObjects(this.scene.children, true);
      
      if (intersects.length > 0) {
        const clicked = intersects[0].object;
        console.log('🖱️ Cliqué sur:', clicked.name);
        
        let obj = clicked;
        let isArcadePart = false;
        while (obj) {
            if (obj.name && obj.name.toLowerCase().includes('arcade')) {
                isArcadePart = true;
                break;
            }
            obj = obj.parent;
        }
        if (isArcadePart && this.cameras.current === this.cameras.arcade) {
            this.openMinecraftGame();
            console.log('🎮 Ouverture MiamCraft');
        }
      }
      });
      console.log('Interactions arcade setup');
  }

  openMinecraftGame() {
    window.open('/static/games/miamcraft/index.html', '_blank');
  }

  setupCameras() {
    // Startin point camera (x, z, y)
    const fov = 55;
    const aspect = isMobile 
    ? Math.min(window.innerWidth / window.innerHeight, 16/9)  // Limite le ratio portrait
    : window.innerWidth / window.innerHeight;

    this.cameras.start = new THREE.PerspectiveCamera(fov, aspect, 1, 1000);
    this.cameras.window = new THREE.PerspectiveCamera(fov, aspect, 1, 1000);
    this.cameras.arcade = new THREE.PerspectiveCamera(fov, aspect, 1, 1000);

    this.cameras.start.position.set(-350, 70, 75);
    
    this.cameras.start.rotation.order = 'YXZ';

    this.cameras.start.userData.lookAtTarget = new THREE.Vector3(100, 70, 100);
    this.cameras.start.lookAt(this.cameras.start.userData.lookAtTarget);

    this.scene.add(this.cameras.start);

    // camera windows point of view
    this.cameras.window = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 1000);
    this.cameras.window.position.set(-200, 70, 75);

    this.cameras.window.rotation.order = 'YXZ';

    this.cameras.window.userData.lookAtTarget = new THREE.Vector3(100, 100, 150);
    this.cameras.window.lookAt(this.cameras.window.userData.lookAtTarget);

    this.scene.add(this.cameras.window);

    // camera arcade Borne
    this.cameras.arcade = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 1000);
    this.cameras.arcade.position.set(-250, 70, 100);

    this.cameras.arcade.rotation.order = 'YXZ';

    this.cameras.arcade.userData.lookAtTarget = new THREE.Vector3(100, 100, 170);
    this.cameras.arcade.lookAt(this.cameras.arcade.userData.lookAtTarget);

    this.scene.add(this.cameras.arcade);

    this.cameras.current = this.cameras.start;
    this.camera = this.cameras.current;

    this.targetRotation.x = this.camera.rotation.x;
    this.targetRotation.y = this.camera.rotation.y;
    this.currentRotation.x = this.camera.rotation.x;
    this.currentRotation.y = this.camera.rotation.y;

    console.log('✅ Caméras configurées');
    console.log('📷 Rotation initiale:', this.camera.rotation);
    console.log('🎯 Target/Current rotation:', this.targetRotation, this.currentRotation);
  }

  setupPaths() {
    this.paths = {
      toWindow: {
        curve: new THREE.CatmullRomCurve3([
          new THREE.Vector3(-350, 70, 75),  // Start
          new THREE.Vector3(-300, 70, 80),  // Point intermédiaire
          new THREE.Vector3(-250, 70, 78),  // Point intermédiaire
          new THREE.Vector3(-200, 70, 75)    // Fenêtre
        ]),
        lookAtPoints: [
          new THREE.Vector3(100, 70, 100),
          new THREE.Vector3(100, 80, 125),
          new THREE.Vector3(100, 90, 135),
          new THREE.Vector3(100, 100, 150)
        ]
      },
      
      toArcade: {
        curve: new THREE.CatmullRomCurve3([
          new THREE.Vector3(-350, 70, 75),  // Start
          new THREE.Vector3(-300, 70, 85),  // Point intermédiaire
          new THREE.Vector3(-250, 70, 120),    // Point intermédiaire
          new THREE.Vector3(-200, 70, 170)     // Arcade
        ]),
        lookAtPoints: [
          new THREE.Vector3(100, 70, 100),
          new THREE.Vector3(100, 80, 125),
          new THREE.Vector3(100, 90, 145),
          new THREE.Vector3(100, 100, 170)
        ]
      }
    };

    if (this.debug) {
      Object.values(this.paths).forEach(path => {
        const points = path.curve.getPoints(50);
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({ color: 0xff0000 });
        const line = new THREE.Line(geometry, material);
        this.scene.add(line);
      });
    }
  }

  setupNavigation() {
    const navUI = document.createElement('div');
    navUI.id = 'camera-navigation';
    navUI.innerHTML = `
      <button id="nav-window" class="nav-btn">
        <span>ViewPoint</span>
      </button>
      <button id="nav-arcade" class="nav-btn">
        <span>Arcade</span>
      </button>
    `;
    document.body.appendChild(navUI)
    document.getElementById('nav-window').addEventListener('click', () => {
      this.animateCameraTo('window')
    });

    document.getElementById('nav-arcade').addEventListener('click', () => {
      this.animateCameraTo('arcade')
    });
  }

  animateCameraTo(destination) {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    this.animationProgress = 0;
    
    const path = destination === 'window' ? 
      this.paths.toWindow : 
      this.paths.toArcade;
    
    const duration = 3000; // 3 secondes
    const startTime = Date.now();
    const endTarget = this.cameras[destination].userData.lookAtTarget;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing (ease-in-out)
      const eased = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      // Position le long de la courbe
      const point = path.curve.getPoint(eased);
      this.camera.position.copy(point);

            // Interpoler le lookAt
            const lookAtIndex = Math.floor(eased * (path.lookAtPoints.length - 1));
            const lookAtNext = Math.min(lookAtIndex + 1, path.lookAtPoints.length - 1);
            const lookAtProgress = (eased * (path.lookAtPoints.length - 1)) - lookAtIndex;
            const lookAt = new THREE.Vector3().lerpVectors(
              path.lookAtPoints[lookAtIndex],
              path.lookAtPoints[lookAtNext],
              lookAtProgress
            );
            this.camera.lookAt(lookAt);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              this.camera.lookAt(endTarget)
              this.targetRotation.x = this.camera.rotation.x;
              this.targetRotation.y = this.camera.rotation.y;
              this.currentRotation.x = this.camera.rotation.x;
              this.currentRotation.y = this.camera.rotation.y;
              this.cameras.current = this.cameras[destination];
              this.isAnimating = false;
              console.log(`✅ Arrivé à: ${destination}`);
            }
          };
          animate();
        }

  goToArcadeFromContact() {
    this.animateCameraTo('arcade');
  }

  updateCameraRotation() {
    if (this.isAnimating) return;

    this.currentRotation.x += (this.targetRotation.x - this.currentRotation.x) * this.dampingFactor;
    this.currentRotation.y += (this.targetRotation.y - this.currentRotation.y) * this.dampingFactor;

    this.camera.rotation.x = this.currentRotation.x;
    this.camera.rotation.y = this.currentRotation.y;
    this.camera.rotation.order = 'YXZ';
  }

  updateDayNightCycle(delta) {
    if (!this.dayNightCycle) return;

    this._dayNightAccum = (this._dayNightAccum || 0) + delta;
    if (this._dayNightAccum < 0.1) return;
    this._dayNightAccum = 0;

    this. _elapsedTime = (this._elapsedTime || 0) + 0.1 * this.dayNightSpeed;
    const sinValue = Math.sin(this._elapsedTime);
    const normalizedTime = (sinValue + 1) / 2;
    const brightness = this.minBrightness + (this.maxBrightness - this.minBrightness) * normalizedTime;

    this.renderer.toneMappingExposure = brightness;

    if (this.scene.environmentIntensity !== undefined) {
      this.scene.environmentIntensity = brightness;
    }

    if (this.indoorLights) {
      this.indoorLights.forEach((light) => {
        if (light.isAmbientLight) {
          light.intensity = 0.15 + (1.0 - normalizedTime) * 1.2;
        } else if (light.isPointLight) {
          light.intensity = (1.0 - normalizedTime) * 25;
        }
      });
    }
  }
  
  animate() {
    requestAnimationFrame(() => this.animate());

    const delta = this.clock.getDelta();

    // cap on 60fps
    this._fpsAccum = (this._fpsAccum || 0) + delta;
    if (this._fpsAccum < 1 / 60) return;
      this._fpsAccum = 0;
    
    if (this.mixer) {
      this.mixer.update(delta);
    }

    if (this.textMesh && this.textMesh.material) {
      this.textBlinkTime += delta * 3;
      // sinusoidal blinking
      const blinkIntensity = (Math.sin(this.textBlinkTime * 3) + 1) / 2;  // 0 à 1
      this.textMesh.material.emissiveIntensity = 0.5 + (blinkIntensity * 1.5);  // 0.5 à 2.0
    }

    if (this.water) {
      this.water.material.uniforms['time'].value += delta * 5.0;
    }

    this.updateDayNightCycle(delta);
    this.updateCameraRotation();

    if (isLowPower) {
      this.renderer.render(this.scene, this.camera);
    } else {
      this.composer.render(); 
    }
  }
  
  onResize() {
    const { width, height } = getRenderSize();
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);

    if (this.composer) {
      this.composer.setSize(width, height);
    }

    if (this.bloomPass) {
      this.bloomPass.resolution.set(width, height);
    }
  }
}

export { OutdoorScene };